import IMask, { type MaskedRangeOptions } from 'imask';

export enum DateFieldFormat {
  MM_DD_YYYY = 'MM/DD/YYYY',
  DD_MM_YYYY = 'DD/MM/YYYY',
  YYYY_MM_DD = 'YYYY-MM-DD',
  DD_DOT_MM_YYYY = 'DD.MM.YYYY',
}

interface DateFormatConfig {
  format: DateFieldFormat;
  mask: string;
  placeholder: string;
  blocks: Record<string, MaskedRangeOptions>;
  parse: (value: string) => { day: number; month: number; year: number } | null;
}

export function isValidDate(day: number, month: number, year: number): boolean {
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

export function validateDateString(value: string, format: DateFieldFormat): boolean {
  const config = getDateMaskConfig(format);
  const parsed = config.parse(value);

  if (!parsed) {
    return false;
  }

  return isValidDate(parsed.day, parsed.month, parsed.year);
}

export function getDateMaskConfig(format: DateFieldFormat): DateFormatConfig {
  const baseBlocks = {
    DD: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 31,
      maxLength: 2,
    },
    MM: {
      mask: IMask.MaskedRange,
      from: 1,
      to: 12,
      maxLength: 2,
    },
    YYYY: {
      mask: IMask.MaskedRange,
      from: 1900,
      to: 2100,
      maxLength: 4,
    },
  };

  const configs: Record<DateFieldFormat, DateFormatConfig> = {
    [DateFieldFormat.MM_DD_YYYY]: {
      format: DateFieldFormat.MM_DD_YYYY,
      mask: 'MM/DD/YYYY',
      placeholder: '__/__/____',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match) return null;
        return { month: parseInt(match[1]!, 10), day: parseInt(match[2]!, 10), year: parseInt(match[3]!, 10) };
      },
    },
    [DateFieldFormat.DD_MM_YYYY]: {
      format: DateFieldFormat.DD_MM_YYYY,
      mask: 'DD/MM/YYYY',
      placeholder: '__/__/____',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!match) return null;
        return { day: parseInt(match[1]!, 10), month: parseInt(match[2]!, 10), year: parseInt(match[3]!, 10) };
      },
    },
    [DateFieldFormat.YYYY_MM_DD]: {
      format: DateFieldFormat.YYYY_MM_DD,
      mask: 'YYYY-MM-DD',
      placeholder: '____-__-__',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;
        return { year: parseInt(match[1]!, 10), month: parseInt(match[2]!, 10), day: parseInt(match[3]!, 10) };
      },
    },
    [DateFieldFormat.DD_DOT_MM_YYYY]: {
      format: DateFieldFormat.DD_DOT_MM_YYYY,
      mask: 'DD.MM.YYYY',
      placeholder: '__.__.____',
      blocks: baseBlocks,
      parse: (value: string) => {
        const match = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (!match) return null;
        return { day: parseInt(match[1]!, 10), month: parseInt(match[2]!, 10), year: parseInt(match[3]!, 10) };
      },
    },
  };

  return configs[format];
}

export function convertToISO(value: string, format: DateFieldFormat): string | null {
  const config = getDateMaskConfig(format);
  const parsed = config.parse(value);
  if (!parsed) return null;

  const { day, month, year } = parsed;
  if (!isValidDate(day, month, year)) return null;

  const dd = String(day).padStart(2, '0');
  const mm = String(month).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

export function convertFromISO(isoString: string, format: DateFieldFormat): string | null {
  const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = match[1]!;
  const month = match[2]!;
  const day = match[3]!;

  switch (format) {
    case DateFieldFormat.MM_DD_YYYY:
      return `${month}/${day}/${year}`;
    case DateFieldFormat.DD_MM_YYYY:
      return `${day}/${month}/${year}`;
    case DateFieldFormat.YYYY_MM_DD:
      return `${year}-${month}-${day}`;
    case DateFieldFormat.DD_DOT_MM_YYYY:
      return `${day}.${month}.${year}`;
  }
}
